import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Длинный список рисуем частями: сначала первые `initial` строк, дальше по
 * `step`, когда прокрутка доходит до конца (невидимый «маячок» после списка).
 *
 * Зачем: окна выбора работ и людей рисовали всё сразу (тысячи строк MUI) —
 * открытие подвисало на секунду-две, на айпаде заметнее (10/2026). Поиск и
 * фильтры работают по всему списку; режется только отрисовка.
 *
 * resetKey — смена поиска/фильтра/сортировки/открытия: снова с начала. На
 * смену самих items (например, отметили строку) не сбрасываем — иначе список
 * под пальцем укорачивался бы.
 */
export default function useProgressiveList(items, { initial = 60, step = 150, resetKey } = {}) {
    const [count, setCount] = useState(initial);
    useEffect(() => { setCount(initial); }, [resetKey, initial]);

    const observerRef = useRef(null);
    const nodeRef = useRef(null);
    const total = items.length;
    const hasMore = count < total;

    // Ref-коллбэк на «маячок»: подключаем наблюдатель, как только он появился.
    const sentinelRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        nodeRef.current = node;
        if (!node) return;
        if (typeof IntersectionObserver === 'undefined') {
            // Без наблюдателя (старый браузер, тесты) — всё сразу, как раньше.
            setCount(Number.MAX_SAFE_INTEGER);
            return;
        }
        observerRef.current = new IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) setCount(c => c + step);
        }, { rootMargin: '600px 0px' });
        observerRef.current.observe(node);
    }, [step]);

    // Дорисовали порцию, а маячок всё ещё в зоне видимости (длинный экран) —
    // наблюдатель сам второй раз не сработает: переподключаем, он сообщит заново.
    useEffect(() => {
        const obs = observerRef.current;
        const node = nodeRef.current;
        if (obs && node && hasMore) {
            obs.unobserve(node);
            obs.observe(node);
        }
    }, [count, hasMore]);

    useEffect(() => () => observerRef.current?.disconnect(), []);

    return {
        visible: hasMore ? items.slice(0, count) : items,
        hasMore,
        sentinelRef,
    };
}
